import { useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Typography,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { useTheme } from '@/contexts/ThemeContext'
import { formatAddress } from '@/utils/formatting'
import { formatUnits } from 'viem'
import { formatDate } from './utils'
import TransactionDetailsModal from './TransactionDetailsModal'
import type { TransactionHistoryItem } from './types'
import type { Chain } from 'viem'

interface TransactionHistoryCardProps {
  transaction: TransactionHistoryItem
  chain: Chain | undefined
  isMobileOrTablet: boolean
  decimals: number
  onCopyAddress: (address: string) => void
}

export default function TransactionHistoryCard({
  transaction,
  chain,
  isMobileOrTablet,
  decimals,
  onCopyAddress,
}: TransactionHistoryCardProps) {
  const { colors } = useTheme()
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const chipTextStyles = {
    backgroundColor: colors.overlay,
    color: colors.text,
    fontFamily: "'TWKEverett', sans-serif",
    fontSize: '1rem',
  }

  const chipContainerStyles = {
    ...chipTextStyles,
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 8px',
    borderRadius: '4px',
  }

  const labelTypographyStyles = {
    color: colors.yellow,
    fontFamily: "'TWKEverett', sans-serif",
    fontSize: '1rem',
    fontWeight: 600,
    mr: 0.5,
  }

  const valueTypographyStyles = {
    color: colors.text,
    fontFamily: "'TWKEverett', sans-serif",
    fontSize: '1rem',
  }
  const {
    tx,
    type,
    safeAddress,
    executionDate,
    transactionHash,
    isSuccessful,
    executor,
    amount,
    to,
  } = transaction

  let statusLabel: string
  let statusBackgroundColor: string
  if (isSuccessful === false) {
    statusLabel = 'Failed'
    statusBackgroundColor = colors.error
  } else if (isSuccessful === true) {
    statusLabel = 'Success'
    statusBackgroundColor = colors.green
  } else {
    statusLabel = 'Unknown'
    statusBackgroundColor = colors.overlay
  }

  if (!safeAddress) return null

  return (
    <Card
      sx={{
        mb: 2,
        backgroundColor: colors.overlay,
        border: `1px solid ${colors.primary}`,
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              flex: 1,
              minWidth: 0,
            }}
          >
            {type && (
              <Chip
                label={type}
                size="small"
                sx={{
                  backgroundColor: colors.primary,
                  color: colors.background,
                  fontWeight: 600,
                }}
              />
            )}
            <Chip
              label={statusLabel}
              size="small"
              sx={{
                backgroundColor: statusBackgroundColor,
                color: colors.background,
                fontWeight: 600,
              }}
            />
          </Box>
          {transactionHash && chain?.blockExplorers?.default?.url && (
            <Button
              variant="outlined"
              size="small"
              href={`${chain.blockExplorers.default.url}/tx/${transactionHash}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                borderColor: colors.primary,
                color: colors.primary,
                whiteSpace: 'nowrap',
                '&:hover': {
                  borderColor: colors.primary,
                  backgroundColor: `${colors.primary}20`,
                },
              }}
            >
              View on Explorer
            </Button>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            mb: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            {amount && (
              <Box sx={chipContainerStyles}>
                <Typography component="span" sx={labelTypographyStyles}>
                  Amount:
                </Typography>
                <Typography component="span" sx={valueTypographyStyles}>
                  {formatUnits(BigInt(amount), decimals)}
                </Typography>
              </Box>
            )}
            {to && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Box sx={chipContainerStyles}>
                  <Typography component="span" sx={labelTypographyStyles}>
                    {type === 'Delete' || type === 'Block' || type === 'Unblock'
                      ? 'Operational Safe:'
                      : 'To:'}
                  </Typography>
                  <Typography component="span" sx={valueTypographyStyles}>
                    {isMobileOrTablet ? formatAddress(to) : to}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => onCopyAddress(to)}
                  sx={{
                    color: colors.primary,
                    padding: '4px',
                    '&:hover': {
                      backgroundColor: `${colors.primary}20`,
                    },
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Box sx={chipContainerStyles}>
              <Typography component="span" sx={labelTypographyStyles}>
                Nonce:
              </Typography>
              <Typography component="span" sx={valueTypographyStyles}>
                {tx.data.nonce}
              </Typography>
            </Box>
            {executor && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Box sx={chipContainerStyles}>
                  <Typography component="span" sx={labelTypographyStyles}>
                    Executor:
                  </Typography>
                  <Typography component="span" sx={valueTypographyStyles}>
                    {isMobileOrTablet ? formatAddress(executor) : executor}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => onCopyAddress(executor)}
                  sx={{
                    color: colors.primary,
                    padding: '4px',
                    '&:hover': {
                      backgroundColor: `${colors.primary}20`,
                    },
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
          {executionDate && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Box sx={chipContainerStyles}>
                <Typography component="span" sx={labelTypographyStyles}>
                  Executed:
                </Typography>
                <Typography component="span" sx={valueTypographyStyles}>
                  {formatDate(executionDate)}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 2,
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={() => setIsDetailsModalOpen(true)}
            sx={{
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': {
                borderColor: colors.primary,
                backgroundColor: `${colors.primary}20`,
              },
            }}
          >
            Details
          </Button>
        </Box>
      </CardContent>
      <TransactionDetailsModal
        open={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        transaction={transaction}
        chain={chain}
        decimals={decimals}
      />
    </Card>
  )
}
